import {useRef,useState} from 'react';
import {Upload} from 'lucide-react';
import {useUploadOperation} from '../hooks/useUploadOperation';
import type {UploadProgressHandler} from '../upload-progress';
import {SAMPLE_BOOK_NAME} from '../first-run';
import sampleBookUrl from '../assets/samples/linear-algebra-primer.pdf?url';
import {EmptyReader} from './Readers';
import {UploadStatus} from './UploadStatus';
import {UploadCancelDialog} from './UploadCancelDialog';
import '../first-run.css';

type LibraryUpload=(files:File[],onProgress:UploadProgressHandler,signal:AbortSignal)=>Promise<unknown>;
/** Reader pane without open tabs. It stays mounted across the first upload batch:
 *  the catalog gains its first file before the batch ends, and unmounting would cancel the rest. */
export function FirstRunReader({ready,hasMaterial,onUpload}:{ready:boolean;hasMaterial:boolean;onUpload:LibraryUpload}){
 const operation=useUploadOperation(),input=useRef<HTMLInputElement>(null),[dragging,setDragging]=useState(false);
 if(!ready)return <div className="reader-empty" aria-busy="true"/>;
 if(hasMaterial&&!operation.busy&&!operation.error)return <EmptyReader/>;
 function upload(files:File[]){if(files.length&&!operation.busy)void operation.start((onProgress,signal)=>onUpload(files,onProgress,signal));}
 function openSample(){
  if(operation.busy)return;
  void operation.start(async(onProgress,signal)=>{
   const response=await fetch(sampleBookUrl,{signal});
   if(!response.ok)throw new Error('示例资料加载失败，请重试。');
   return onUpload([new File([await response.blob()],SAMPLE_BOOK_NAME,{type:'application/pdf'})],onProgress,signal);
  });
 }
 const acceptsFiles=(event:React.DragEvent)=>event.dataTransfer.types.includes('Files');
 return <div className={`reader-empty first-run-reader${dragging?' dragging':''}`}
  onDragOver={event=>{if(!acceptsFiles(event)||operation.busy)return;event.preventDefault();event.dataTransfer.dropEffect='copy';setDragging(true);}}
  onDragLeave={event=>{if(!event.currentTarget.contains(event.relatedTarget as Node|null))setDragging(false);}}
  onDrop={event=>{if(!acceptsFiles(event))return;event.preventDefault();setDragging(false);upload([...event.dataTransfer.files]);}}>
  {operation.busy?<UploadStatus progress={operation.progress} destination="资料库" onCancel={operation.requestCancel} cancelling={operation.cancelling} cleanupFailed={operation.cleanupFailed} onRetry={()=>void operation.retryCleanup()}/>:<>
   <button type="button" className="first-run-drop" onClick={()=>input.current?.click()}><Upload size={26} strokeWidth={1.6} aria-hidden="true"/><strong>上传或拖入资料</strong><span>PDF、Markdown、视频等任意格式</span></button>
   <button type="button" className="first-run-sample" onClick={openSample}>打开示例</button>
  </>}
  {operation.error&&<p className="first-run-error" role="alert">{operation.error}</p>}
  {!operation.error&&operation.status&&<p className="first-run-status" role="status">{operation.status}</p>}
  <input ref={input} type="file" multiple hidden onChange={event=>{upload([...event.target.files??[]]);event.target.value='';}}/>
  <UploadCancelDialog open={operation.confirming} onConfirm={operation.confirmCancel} onClose={operation.dismissCancel}/>
 </div>;
}
